import axios, { AxiosRequestHeaders, CancelTokenSource } from "axios";
import { acquireToken } from "@/authentication/TokenService";
import { Configuration } from "@/config";

const {
  config: {
    API_ENDPOINT,
    INTEGRATION_API_ENDPOINT,
    FILE_API_ENDPOINT,
    OPPORTUNITY_API_ENDPOINT,
    PLATFORM_OPS_API_ENDPOINT,
    JOB_ENGINE_API_ENDPOINT,
    PCS_SOFTWARE_CUSTOMER_ID,
  },
} = new Configuration();

export const api = axios.create({
  withCredentials: true,
  baseURL: API_ENDPOINT,
});

export const fileApi = axios.create({
  withCredentials: true,
  baseURL: FILE_API_ENDPOINT,
});

export const integrationApi = axios.create({
  withCredentials: true,
  baseURL: INTEGRATION_API_ENDPOINT,
});

export const opportunityApi = axios.create({
  withCredentials: true,
  baseURL: OPPORTUNITY_API_ENDPOINT,
});

export const platformOpsApi = axios.create({
  withCredentials: true,
  baseURL: PLATFORM_OPS_API_ENDPOINT,
});

export const jobEngineApi = axios.create({
  withCredentials: true,
  baseURL: JOB_ENGINE_API_ENDPOINT,
});

// Reusable request interceptor factory
const createRequestInterceptor = (includeContentType = true) => async (config: any) => {
  const accessToken = await acquireToken(false);

  const existingHeaders = (config.headers || {}) as Record<string, any>;
  const customerId =
    existingHeaders["Customer-Id"] ?? existingHeaders["customer-id"] ?? PCS_SOFTWARE_CUSTOMER_ID;

  const headers: any = {
    ...existingHeaders,
    Authorization: `Bearer ${accessToken}`,
    "Customer-Id": customerId,
  };

  if (includeContentType && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  config.headers = headers as unknown as AxiosRequestHeaders;
  return config;
};

// Reusable response interceptor factory
const createResponseInterceptor = (apiInstance: typeof api | typeof fileApi) => [
  (response: any) => response,
  async function (error: any) {
    const originalRequest = error.config;
    if (error.response?.status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;
      const accessToken = await acquireToken(true);
      axios.defaults.headers.common["Authorization"] = "Bearer " + accessToken;
      return apiInstance(originalRequest);
    }
    return Promise.reject(error);
  }
];

// Reusable error handler for request interceptors
const requestErrorHandler = (error: any) => Promise.reject(error);

// Apply interceptors to main API (with Content-Type)
api.interceptors.request.use(createRequestInterceptor(true), requestErrorHandler);
api.interceptors.response.use(...createResponseInterceptor(api));

// Apply interceptors to File API (without Content-Type for file uploads)
fileApi.interceptors.request.use(createRequestInterceptor(false), requestErrorHandler);
fileApi.interceptors.response.use(...createResponseInterceptor(fileApi));

// Apply interceptors to Integration API (with Content-Type)
integrationApi.interceptors.request.use(createRequestInterceptor(true), requestErrorHandler);
integrationApi.interceptors.response.use(...createResponseInterceptor(integrationApi));

// Apply interceptors to Opportunity API (with Content-Type)
opportunityApi.interceptors.request.use(createRequestInterceptor(true), requestErrorHandler);
opportunityApi.interceptors.response.use(...createResponseInterceptor(opportunityApi));

// Apply interceptors to Platform Ops API (with Content-Type)
platformOpsApi.interceptors.request.use(createRequestInterceptor(true), requestErrorHandler);
platformOpsApi.interceptors.response.use(...createResponseInterceptor(platformOpsApi));


// Apply request interceptor to JobEngine API (with Content-Type)
jobEngineApi.interceptors.request.use(createRequestInterceptor(true), requestErrorHandler);

// JobEngine API uses a DIFFERENT response interceptor: retry on 401 (token stale) only, NOT 403 (privilege denial).
// 403 must bubble up to the UI so pages can render "access-denied" messages.
jobEngineApi.interceptors.response.use(
  (response) => response,
  async function (error) {
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const accessToken = await acquireToken(true);
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return jobEngineApi(originalRequest);
    }
    return Promise.reject(error);
  }
);
